import { useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useClinica } from '@/contexts/ClinicaContext';
import { toast } from 'sonner';
import type { CreateReportData, ReportRequestPayload, GenerateAIReportV2Response } from '@/types/aiReports';

/**
 * Hook para criação de relatórios de IA (Versão Nativa Edge Function)
 * 
 * O que faz:
 * - Gerencia o processo de criação de novos relatórios no modelo V2
 * - Envia o `lead_filter` com as opções: 'all' | 'ads' | 'links'
 * - Chama a nova Edge Function `generate-ai-report-v2`
 */
export const useCreateAIReportV2 = (refetchReports: () => void, targetClinicaId?: string, isAdminMode?: boolean) => {
    const { clinicaId: contextClinicaId } = useClinica();

    const createReportMutation = useMutation({
        mutationFn: async (reportData: CreateReportData) => {
            const effectiveClinicaId = targetClinicaId || contextClinicaId;

            if (!effectiveClinicaId) {
                throw new Error('ID da clínica não encontrado');
            }

            console.log('📊 Criando novo relatório (V2) para clínica:', effectiveClinicaId, reportData);

            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                throw new Error('Usuário não autenticado');
            }

            const { data: reportRecord, error: createError } = await supabase
                .from('ai_reports')
                .insert({
                    clinica_id: effectiveClinicaId,
                    start_date: reportData.period_start.toISOString(),
                    end_date: reportData.period_end.toISOString(),
                    delivery_method: reportData.delivery_method,
                    phone_number: reportData.recipient_phone_number,
                    status: 'pending'
                })
                .select()
                .single();

            if (createError || !reportRecord) {
                console.error('Erro ao criar registro do relatório:', createError);
                throw createError;
            }

            console.log('✅ Registro do relatório criado:', reportRecord.id);

            const payload: ReportRequestPayload = {
                clinica_id: effectiveClinicaId,
                start_date: reportData.period_start.toISOString(),
                end_date: reportData.period_end.toISOString(),
                delivery_method: reportData.delivery_method,
                recipient_phone_number: reportData.recipient_phone_number,
                report_request_id: reportRecord.id,
                lead_filter: reportData.lead_filter || 'all',
                requested_by_admin: isAdminMode || false,
                requester_user_id: user.id,
                admin_clinic_context: isAdminMode ? contextClinicaId : null
            };

            const { data: functionResponse, error: functionError } = await supabase.functions.invoke<GenerateAIReportV2Response>('generate-ai-report-v2', {
                body: payload
            });

            if (functionError) {
                console.error('Erro na Edge Function V2:', functionError);

                // Atualizar status para falha se a function simplesmente falhar de conectar localmente
                await supabase
                    .from('ai_reports')
                    .update({
                        status: 'failed',
                        error_message: functionError.message || 'Erro de comunicação na Edge Function'
                    })
                    .eq('id', reportRecord.id);

                throw functionError;
            }

            console.log('✅ Edge Function V2 executada com sucesso:', functionResponse);
            return {
                reportRecord,
                tokenUsage: functionResponse?.token_usage || null
            };
        },
        onSuccess: (result) => {
            if (isAdminMode && result?.tokenUsage) {
                const tokenUsage = result.tokenUsage;
                const totalTokens = Number(tokenUsage.total_tokens || 0).toLocaleString('pt-BR');
                const promptTokens = Number(tokenUsage.prompt_tokens || 0).toLocaleString('pt-BR');
                const completionTokens = Number(tokenUsage.completion_tokens || 0).toLocaleString('pt-BR');
                const costLabel = typeof tokenUsage.estimated_cost_usd === 'number'
                    ? ` | Custo estimado: US$ ${tokenUsage.estimated_cost_usd.toFixed(6)}`
                    : '';

                toast.success(
                    `Relatório V2 concluído. Tokens total: ${totalTokens} (entrada ${promptTokens} | saída ${completionTokens})${costLabel}`
                );
            } else {
                toast.success('Relatório V2 solicitado com sucesso! Acompanhe o status nesta tela.');
            }
            refetchReports();
        },
        onError: (error) => {
            console.error('❌ Erro ao criar relatório V2:', error);
            toast.error('Erro ao solicitar relatório V2. Tente novamente.');
        }
    });

    return {
        createReport: createReportMutation.mutate,
        isCreating: createReportMutation.isPending
    };
};
